import { Badge } from '@/components/ui/Badge';
import { LevelPill, Section, timeLabel, DISPOSITION_LABEL } from '../ui';
import type { Workspace } from '../types';

interface Event { at: string; icon: string; text: string; who?: string | null }

/**
 * The encounter at a glance: triage history (D5 — every assessment, never
 * overwritten) and a timeline assembled from the record's own rows. Nothing
 * here is stored; it is the same read-layer idea as the IP Case Sheet.
 */
export default function OverviewSection({ ws }: { ws: Workspace }) {
  const enc = ws.encounter;
  const events: Event[] = [];
  events.push({ at: enc.arrived_at, icon: '🚑', text: `Arrived${enc.arrival_mode ? ` (${enc.arrival_mode.replace('_', ' ')})` : ''}` });
  for (const t of ws.triage) events.push({ at: t.assessed_at, icon: t.is_retriage ? '🔁' : '🩺', text: `${t.is_retriage ? 'Re-triaged' : 'Triaged'} ${t.level_label}${t.override_reason ? ` — override: ${t.override_reason}` : ''}`, who: t.assessed_by });
  if (enc.first_doctor_at) events.push({ at: enc.first_doctor_at, icon: '👨‍⚕️', text: `Seen by doctor${enc.attending_doctor_name ? ` (${enc.attending_doctor_name})` : ''}` });
  for (const n of ws.notes) events.push({ at: n.note_datetime, icon: '📝', text: `${n.note_type} note${n.status === 'draft' ? ' (draft)' : ''}${n.version > 1 ? ` v${n.version}` : ''}`, who: n.created_by });
  for (const o of ws.med_orders) {
    events.push({ at: o.ordered_at, icon: '💊', text: `${o.is_verbal_order ? 'Verbal order' : 'Ordered'}: ${o.drug_name} ${o.dose_quantity ? `${Number(o.dose_quantity)} ${o.dose_unit ?? ''}` : ''} ${o.route ?? ''} ${o.frequency_code}`, who: o.ordered_by });
    for (const a of o.administrations) events.push({ at: a.administered_at, icon: a.status === 'given' ? '💉' : '⛔', text: `${o.drug_name} ${a.status.replace('_', ' ')}${a.reason ? ` — ${a.reason}` : ''}`, who: a.administered_by });
  }
  for (const p of ws.procedures) events.push({ at: p.started_at, icon: '🩹', text: `Procedure: ${p.procedure_name}${p.outcome ? ` (${p.outcome})` : ''}`, who: p.performed_by });
  for (const c of ws.consults) {
    events.push({ at: c.requested_at, icon: '📞', text: `${c.specialty} consult requested (${c.urgency})`, who: c.requested_by });
    if (c.arrived_at) events.push({ at: c.arrived_at, icon: '🧑‍⚕️', text: `${c.specialty} attended`, who: c.responded_by });
  }
  for (const l of ws.labs) events.push({ at: l.ordered_at, icon: '🧪', text: `Lab ${l.order_number}: ${l.items.map((i) => i.test_name).join(', ')} (${l.priority})`, who: l.ordering_doctor_name });
  for (const c of ws.critical_results) events.push({ at: c.detected_at, icon: '❗', text: `Critical: ${c.parameter_name} ${c.value_display}${c.acknowledged_at ? ' — acknowledged' : ''}` });
  if (enc.observation_started_at) events.push({ at: enc.observation_started_at, icon: '⌛', text: 'Observation started' });
  if (enc.decision_at && enc.disposition) events.push({ at: enc.decision_at, icon: '➜', text: `Disposition: ${DISPOSITION_LABEL[enc.disposition]}`, who: enc.disposition_by });
  if (enc.departed_at) events.push({ at: enc.departed_at, icon: '🚪', text: 'Departed the ED' });
  events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const latest = ws.triage[0];
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Section title="Triage history">
        {ws.triage.length === 0 ? <div className="text-[13px] text-ink-muted">Not triaged yet.</div> : (
          <div className="space-y-2">
            {ws.triage.map((t) => (
              <div key={t.id} className="rounded-lg border border-border p-2.5 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  <LevelPill color={t.level_color} label={t.level_label} />
                  {t.is_retriage && <Badge variant="info">re-triage</Badge>}
                  {t.suggested_level_code && t.suggested_level_code !== t.level_code && <Badge variant="warn">overrode {t.suggested_level_code}</Badge>}
                  <span className="ml-auto text-ink-muted">{timeLabel(t.assessed_at)} · {t.assessed_by}</span>
                </div>
                <div className="mt-1 text-ink-muted">
                  {[
                    t.bp_systolic != null && `BP ${t.bp_systolic}/${t.bp_diastolic ?? '–'}`,
                    t.pulse_rate != null && `HR ${t.pulse_rate}`,
                    t.respiratory_rate != null && `RR ${t.respiratory_rate}`,
                    t.spo2 != null && `SpO₂ ${t.spo2}%`,
                    t.temperature != null && `T ${t.temperature}°C`,
                    t.gcs != null && `GCS ${t.gcs}`,
                    t.avpu && `AVPU ${t.avpu}`,
                    t.pain_score != null && `pain ${t.pain_score}/10`,
                    t.grbs != null && `GRBS ${t.grbs}`,
                  ].filter(Boolean).join(' · ') || 'No vitals recorded'}
                </div>
                {t.early_warning?.news2 && <div className="mt-0.5">NEWS2 {t.early_warning.news2.score} ({t.early_warning.news2.risk}){!t.early_warning.news2.complete && ' — partial'}</div>}
                {t.override_reason && <div className="mt-0.5 italic">Override: {t.override_reason}</div>}
              </div>
            ))}
          </div>
        )}
        {latest?.red_flags && latest.red_flags.length > 0 && (
          <div className="mt-2 text-[12px]"><b>Red flags at last triage:</b> {latest.red_flags.join(', ').replace(/_/g, ' ')}</div>
        )}
      </Section>

      <Section title="Timeline">
        <ol className="space-y-1.5">
          {events.map((e, i) => (
            <li key={i} className="flex gap-2 text-[12px]">
              <span className="w-14 shrink-0 font-mono text-ink-muted">{timeLabel(e.at)}</span>
              <span>{e.icon}</span>
              <span className="flex-1">{e.text}{e.who ? <span className="text-ink-muted"> · {e.who}</span> : null}</span>
            </li>
          ))}
        </ol>
      </Section>

      {ws.open_items.length > 0 && (
        <Section title="Open items" className="lg:col-span-2">
          <ul className="space-y-1 text-[12px]">
            {ws.open_items.map((i, k) => (
              <li key={k} className={i.severity === 'red' ? 'text-danger-ink' : i.severity === 'amber' ? 'text-warn-ink' : 'text-ink-muted'}>• {i.message}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
